import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Button,
  Typography,
  Paper,
} from "@mui/material";
import { ArrowBack, Print, Download } from "@mui/icons-material";
import { useAuth } from "../login/AuthContext";
import axios from "axios";
import config from "../../config/config";
import { toast } from "react-toastify";

const TeklifBelgesi = () => {
  const { teklifId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [belgeData, setBelgeData] = useState(null);
  const [htmlContent, setHtmlContent] = useState("");

  useEffect(() => {
    fetchTeklifBelgesi();
  }, [teklifId]);

  /** XML + XSL ile XSLT ile HTML üretir (teklif.xslt – logo/başlık aynı şablon) */
  const teklifDataToHtml = (xml, xsl, logoDataUri) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "text/xml");
    const xslDoc = parser.parseFromString(xsl, "text/xml");
    if (xmlDoc.querySelector("parsererror") || xslDoc.querySelector("parsererror")) {
      throw new Error("XML veya XSL parse hatası");
    }
    const processor = new XSLTProcessor();
    processor.importStylesheet(xslDoc);
    const resultDoc = processor.transformToDocument(xmlDoc);
    const serializer = new XMLSerializer();
    let html =
      resultDoc && resultDoc.documentElement
        ? serializer.serializeToString(resultDoc)
        : "";
    if (!html && processor.transformToFragment) {
      const frag = processor.transformToFragment(xmlDoc, document);
      if (frag && frag.childNodes && frag.childNodes.length > 0) {
        html = serializer.serializeToString(frag);
        if (!/^\s*<!DOCTYPE/i.test(html) && !/^\s*<html/i.test(html)) {
          html = "<!DOCTYPE html><html><head><meta charset=\"UTF-8\"></head><body>" + html + "</body></html>";
        }
      }
    }
    html = (html || "").replace(/src="[^"]*logo32\.png[^"]*"/gi, 'src="' + (logoDataUri || "") + '"');
    html = html.replace(/src='[^']*logo32\.png[^']*'/gi, "src='" + (logoDataUri || "") + "'");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (origin) {
      html = html.replace(/src="(\/\d+_logo\.png)"/gi, 'src="' + origin + '$1"');
      html = html.replace(/src='(\/\d+_logo\.png)'/gi, "src='" + origin + "$1'");
    }
    if (!/^\s*<!DOCTYPE\s+html/i.test(html)) html = "<!DOCTYPE html>\n" + html;
    return html;
  };

  const fetchTeklifBelgesi = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.baseURL}/teklif-koruma/belge/${teklifId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const data = response.data.data;
        setBelgeData(data);

        // Sunucu xml + xsl döndürdüyse XSLT ile HTML üret (logo/başlık teklif.xslt ile aynı)
        if (data.xml && data.xsl) {
          try {
            const html = teklifDataToHtml(data.xml, data.xsl, data.logoDataUri || "");
            setHtmlContent(html);
          } catch (e) {
            console.warn("XSLT HTML hatası, eski şablon kullanılıyor:", e);
            const belge = data.xmlData?.dizayn?.Belgeler?.Belge;
            if (belge) setHtmlContent(generateHTML(belge));
            else toast.error("Teklif belgesi verisi işlenemedi");
          }
          return;
        }

        // Geriye uyumluluk: sadece xmlData varsa eski generateHTML
        const xmlData = data.xmlData;
        if (xmlData) {
          const belge = xmlData.dizayn?.Belgeler?.Belge;
          if (belge) {
            const html = generateHTML(belge);
            setHtmlContent(html);
          } else {
            toast.error("Teklif belgesi verisi bulunamadı");
          }
        } else {
          toast.error("Teklif belgesi verisi bulunamadı");
        }
      } else {
        toast.error(response.data.message || "Teklif belgesi yüklenemedi");
      }
    } catch (error) {
      console.error("Teklif belgesi yükleme hatası:", error);
      toast.error("Teklif belgesi yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const generateHTML = (belge) => {
    const baslik = belge.Baslik || {};
    const cari = belge.Cari || {};
    const hareketler = belge.Hareketler?.Hareket || [];
    const altToplam = belge.AltToplam || {};
    const sozlesmeler = belge.Sozlesmeler?.Sozlesme || [];
    const firmaBilgileri = belge.FirmaBilgileri || {};

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Teklif Belgesi - ${baslik.BELGENO || ""}</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          @page {
            size: A4;
            margin: 0;
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: "Inter", Arial, sans-serif;
            margin: 0;
            padding: 2cm 3cm;
            width: 21.00cm;
            min-height: 29.7cm;
            margin: 0 auto;
            background: white;
          }
          .page {
            width: 100%;
            min-height: calc(29.7cm - 4cm);
            padding: 0;
            box-sizing: border-box;
            page-break-after: always;
            page-break-inside: avoid;
            margin-bottom: 0.5cm;
          }
          .page:last-child {
            page-break-after: auto;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }
          .logo {
            width: 150px;
          }
          .title {
            font-size: 22pt;
            font-weight: bold;
            text-align: center;
            flex: 1;
          }
          .belge-info {
            text-align: right;
          }
          .musteri-bilgileri {
            margin: 20px 0;
          }
          .musteri-bilgileri table {
            width: 70%;
          }
          .musteri-bilgileri th {
            text-align: left;
            padding-right: 10px;
          }
          .hareketler-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          .hareketler-table th,
          .hareketler-table td {
            border: 1px solid black;
            padding: 8px;
            text-align: left;
          }
          .hareketler-table th {
            background-color: #f0f0f0;
            font-weight: bold;
          }
          .hareketler-table td:last-child,
          .hareketler-table th:last-child {
            text-align: right;
          }
          .toplam-bolumu {
            display: flex;
            justify-content: space-between;
            margin: 20px 0;
            page-break-inside: avoid;
            page-break-before: avoid;
          }
          .toplam-sol {
            width: 65%;
          }
          .toplam-sag {
            width: 35%;
            text-align: right;
          }
          .toplam-sag table {
            width: 100%;
            margin-left: auto;
          }
          .toplam-sag th {
            text-align: right;
            padding-right: 10px;
          }
          .toplam-sag td {
            text-align: right;
          }
          .sozlesme-bolumu {
            margin: 20px 0;
            page-break-inside: avoid;
          }
          .sozlesme-bolumu h3 {
            margin-top: 20px;
            page-break-after: avoid;
            page-break-before: auto;
          }
          .sozlesme-bolumu p {
            margin: 5px 0;
            orphans: 3;
            widows: 3;
            page-break-inside: avoid;
          }
          .alt-tablo {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            page-break-inside: avoid;
          }
          .alt-tablo th,
          .alt-tablo td {
            border: 1px solid black;
            padding: 10px;
            text-align: center;
          }
          .footer {
            margin-top: 20px;
            text-align: center;
          }
          @media print {
            body {
              margin: 0;
              padding: 2cm 3cm;
              width: 21.00cm;
            }
            .no-print {
              display: none;
            }
            .page {
              margin: 0;
              padding: 0;
              box-shadow: none;
              page-break-after: always;
              width: 100%;
            }
            .page:last-child {
              page-break-after: auto;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <img src="/logo32.png" alt="Logo" class="logo" />
          </div>
          <div class="title">${baslik.BELGEBASLIKSTR || "Fiyat Teklifi"}</div>
          <div class="belge-info">
            <div>${baslik.TARIH || ""}</div>
            <div><strong>Belge No: ${baslik.BELGENO || ""}</strong></div>
          </div>
        </div>

        <div class="musteri-bilgileri">
          <table>
            <tr>
              <th>Firma Adı</th>
              <th>:</th>
              <td>${baslik.UNVAN || ""}</td>
            </tr>
            <tr>
              <th>V.Dairesi-No</th>
              <th>:</th>
              <td>${baslik.VERGIDAIRESI || ""} - ${baslik.VERGINO || baslik.TCKIMLIKNO || ""}</td>
            </tr>
            <tr>
              <th>Yetkili</th>
              <th>:</th>
              <td>${cari.ADI || ""} ${cari.SOYADI || ""}</td>
            </tr>
            <tr>
              <th>Mail</th>
              <th>:</th>
              <td>${cari.EMAIL || ""}</td>
            </tr>
            <tr>
              <th>Telefon</th>
              <th>:</th>
              <td>${cari.TELEFON1 || ""}${cari.TELEFON2 ? " / " + cari.TELEFON2 : ""}</td>
            </tr>
            <tr>
              <th>Adres</th>
              <th>:</th>
              <td>${cari.ADRESFATURA || ""}</td>
            </tr>
          </table>
        </div>

        <table class="hareketler-table">
          <thead>
            <tr>
              <th width="33%">Ürün Adı</th>
              <th width="5%">Miktar</th>
              <th width="5%">Birim</th>
              <th width="5%">Kdv</th>
              <th width="10%">B.Fiyat</th>
              <th width="10%">Tutar</th>
            </tr>
          </thead>
          <tbody>
            ${hareketler.map((hareket) => `
              <tr>
                <td>
                  <strong>${hareket.MALINCINSI || ""}</strong>
                  ${hareket.PACIKLAMA ? `<br/>${hareket.PACIKLAMA}` : ""}
                </td>
                <td align="center">${parseFloat(hareket.MIKTAR || 0).toFixed(2)}</td>
                <td align="center">${hareket.BIRIM || ""}</td>
                <td align="center">%${hareket.KDV || 0}</td>
                <td align="right">${parseFloat(hareket.FiyatDov || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${hareket.PARABIRIMI || "TL"}</td>
                <td align="right">${parseFloat(hareket.TutarDov || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${hareket.PARABIRIMI || "TL"}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>

        <div class="toplam-bolumu">
          <div class="toplam-sol">
            <p><strong>Yazıyla:</strong> ${altToplam.DYAZIILE || ""}</p>
          </div>
          <div class="toplam-sag">
            <table>
              <tr>
                <th>Toplam:</th>
                <td>${parseFloat(altToplam.DGORSELTUTAR || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baslik.PARABIRIMI || "TL"}</td>
              </tr>
              <tr>
                <th>Ara Toplam:</th>
                <td>${parseFloat(altToplam.DARATOPLAM || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baslik.PARABIRIMI || "TL"}</td>
              </tr>
              <tr>
                <th>Kdv:</th>
                <td>${parseFloat(altToplam.DGORSELTOPLAMKDV || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baslik.PARABIRIMI || "TL"}</td>
              </tr>
              <tr>
                <th>Genel Toplam:</th>
                <td><strong>${parseFloat(altToplam.DGENELTOPLAM || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baslik.PARABIRIMI || "TL"}</strong></td>
              </tr>
            </table>
          </div>
        </div>

        ${sozlesmeler.map((sozlesme) => `
          <div class="sozlesme-bolumu">
            <h3>${sozlesme.GRUPADI || ""}</h3>
            ${(sozlesme.Maddeler?.MADDE || []).map((madde) => `<p>${madde}</p>`).join("")}
          </div>
        `).join("")}

        <table class="alt-tablo">
          <tr>
            <th style="width:33.3%;">Sipariş Alan</th>
            <th style="width:33.3%;">Onay Notu</th>
            <th style="width:33.3%;">Alıcı Onay (Kaşe-İmza)</th>
          </tr>
          <tr style="height:60px;">
            <td></td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td colspan="3" align="center">
              Bu belge Pozitif Ofis ile oluşturulmuştur.
            </td>
          </tr>
        </table>

        <div class="footer">
          <p>${firmaBilgileri.WEBSITE || "www.pozitifofis.com.tr"}</p>
          ${(firmaBilgileri.SubeBilgileri || []).map((sube) => `
            <p>
              <strong>A:</strong> ${sube.MAHALLE || ""} ${sube.CADDE || ""} ${sube.APARTMANADI || ""} ${sube.APARTMANNO || ""} ${sube.ILCE || ""}/${sube.SEHIR || ""}
              <strong>T:</strong> ${sube.TELEFON || ""}
              <strong>F:</strong> ${sube.FAKS || ""}
              <strong>M:</strong> ${sube.MAIL || ""}
            </p>
          `).join("")}
        </div>
      </body>
      </html>
    `;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%',
        height: '100vh',
        overflow: 'auto',
        backgroundColor: '#f5f5f5',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          backgroundColor: '#fff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 10000
        }}
        className="no-print"
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          Geri
        </Button>
        <Box>
          <Button
            startIcon={<Print />}
            onClick={handlePrint}
            variant="contained"
            sx={{ mr: 2 }}
          >
            Yazdır
          </Button>
          <Button
            startIcon={<Download />}
            onClick={handleDownload}
            variant="contained"
          >
            İndir
          </Button>
        </Box>
      </Box>

      <Box 
        sx={{ 
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Box 
          sx={{ 
            width: '21cm',
            minHeight: '29.7cm',
            backgroundColor: '#fff',
            boxShadow: '0 0 10px rgba(0,0,0,0.2)',
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </Box>
      </Box>
    </Box>
  );
};

export default TeklifBelgesi;
